import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ErrorNavigationService } from './error-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalServices {
  constructor(
    private readonly errorNavigationService: ErrorNavigationService
  ) {}

  /**
   * Creates HTTP headers for the API requests.
   * @param apiKey The API key to include in the headers.
   * @returns HttpHeaders object with necessary headers.
   */
  createHeader(apiKey: string): HttpHeaders {
    const headersConfig: { [key: string]: string } = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (apiKey) {
      headersConfig['Authorization'] = `Bearer ${apiKey}`;
    }

    return new HttpHeaders(headersConfig);
  }

  /**
   * Caches data and manages expiry based on timestamp.
   * @param cache The cache object to store the data.
   * @param key The key under which the data is stored.
   * @param data The data to cache.
   * @param expiryTime The expiry time for the cache in milliseconds.
   * @returns The cached data if not expired, else stores new data.
   */
  cacheData<T>(
    cache: { [key: string]: { data: T; timestamp: number } },
    key: string,
    data: T,
    expiryTime: number
  ): T {
    const cached = cache[key];
    if (cached && Date.now() - cached.timestamp < expiryTime) {
      return cached.data;
    } else {
      cache[key] = { data, timestamp: Date.now() };
      return data;
    }
  }
  /**
   * Handles errors that occur during the API request.
   * Redirects to appropriate error page dynamically for 5xx errors.
   * @param error The error object received from the failed request.
   * @returns An observable that throws an error.
   */
  handleError(
    error: HttpErrorResponse,
    serviceName: string,
    redirect500?: boolean
  ): Observable<never> {
    const errorMessage =
      error.error instanceof Error
        ? `Client-side error at: ${serviceName} with message ${error.error.message}`
        : `Server-side error: ${error.status}, message: ${error.message}`;

    if (error.status >= 500 && error.status < 600 && redirect500) {
      this.errorNavigationService.navigateToErrorPage(error.status.toString());
    }

    return throwError(() => new Error(errorMessage));
  }
}
